import type { ReactNode } from 'react';
import { cx } from '../cx';

export type LifelineMonth = {
  id: string;
  /** Short month label ("Oct"). */ label: ReactNode;
  value: number;
  /** Formatted value. */ display: ReactNode;
};

export type LifelineProps = {
  /** Oldest first. */
  months: LifelineMonth[];
  /** States the finding ("Hiring picked up in spring"). */
  title: ReactNode;
  /** The average line, with its label ("12-month average: 8 jobs a month"). */
  average?: { value: number; label: ReactNode };
  /** Index date and basis, required. */
  source: ReactNode;
  /** Header cells of the hidden data table. */
  tableHeaders: { month: ReactNode; value: ReactNode };
  /** Label every Nth month under the bars (default 3). */
  labelEvery?: number;
  className?: string;
};

/** A share of the bar height, in percent. Pure, exported for tests. */
export const lifelineHeight = (value: number, max: number): number =>
  max > 0 ? Math.round((Math.max(0, value) / max) * 1000) / 10 : 0;

/**
 * An employer's hiring over time: one sun bar per month and a dashed gold average line, with a
 * visually hidden table carrying every value.
 */
export function Lifeline({ months, title, average, source, tableHeaders, labelEvery = 3, className }: LifelineProps) {
  const max = Math.max(1, ...months.map((m) => m.value), average?.value ?? 0);
  return (
    <figure className={cx('m-0 flex flex-col gap-3', className)}>
      <figcaption className="type-subheading text-jc-ink">{title}</figcaption>
      <div aria-hidden="true" className="relative flex h-32 items-end gap-1 border-b border-jc-border">
        {months.map((m) => (
          <span
            key={m.id}
            className="flex-1 rounded-t-jc-info bg-jc-cta"
            style={{ height: `${lifelineHeight(m.value, max)}%` }}
          />
        ))}
        {average ? (
          <span
            className="absolute inset-x-0 border-t-2 border-dashed border-jc-ink"
            style={{ bottom: `${lifelineHeight(average.value, max)}%` }}
          />
        ) : null}
      </div>
      <div aria-hidden="true" className="flex gap-1 text-xs text-jc-ink-secondary">
        {months.map((m, i) => (
          <span key={m.id} className="flex-1 text-center">
            {i % labelEvery === 0 ? m.label : ''}
          </span>
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-jc-ink-secondary">
        {average ? (
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden="true" className="w-4 border-t-2 border-dashed border-jc-ink" />
            {average.label}
          </span>
        ) : (
          <span />
        )}
        <span>{source}</span>
      </div>
      <table className="sr-only">
        <thead>
          <tr>
            <th scope="col">{tableHeaders.month}</th>
            <th scope="col">{tableHeaders.value}</th>
          </tr>
        </thead>
        <tbody>
          {months.map((m) => (
            <tr key={m.id}>
              <th scope="row">{m.label}</th>
              <td>{m.display}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
